import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { API } from '../api/api_setup';
import { useAuth } from './useAuth';
import { useSnackbar } from './useSnackbar';

const NotificationContext = createContext();

const extractPerformanceScore = (message) => {
  if (!message) {
    return null;
  }
  const match = message.match(/Performance Score:\s*([0-9]+(?:\.[0-9]+)?)/i);
  if (!match) {
    return null;
  }
  const value = Number(match[1]);
  return Number.isNaN(value) ? null : value;
};

const extractUserEmail = (userDetails) => {
  if (!userDetails) return null;
  if (typeof userDetails === 'object') {
    return userDetails.email || null;
  }
  return userDetails;
};

const parseMetadata = (metadataStr) => {
  if (!metadataStr) return null;
  try {
    return typeof metadataStr === 'string' ? JSON.parse(metadataStr) : metadataStr;
  } catch {
    return null;
  }
};

const normalizeNotification = (item) => {
  const performanceScore = extractPerformanceScore(item.message);
  const relatedAlert = item.related_alert;
  const metadata = parseMetadata(relatedAlert?.alert_metadata);
  const metrics = metadata?.metrics_used || {};
  const thresholds = metadata?.thresholds_used || {};
  const weightBreakdown = metadata?.weight_breakdown || {};
  const penalties = metadata?.penalties || {};
  const weightScore = relatedAlert?.weight_score;

  return {
    id: item.id,
    cluster: item.cluster_name,
    type: item.notification_type === 'scanning_alert' ? 'Scan' : 'Rescan',
    severity: item.severity,
    title: item.title,
    message: item.message,
    time: item.created_at,
    createdAt: item.created_at,
    dataTimestamp: item.data_timestamp,
    readBy: extractUserEmail(item.read_by),
    readByDetails: item.read_by,
    acknowledgedBy: extractUserEmail(item.acknowledged_by),
    acknowledgedByDetails: item.acknowledged_by,
    isRead: Boolean(item.is_read),
    isAcknowledged: Boolean(item.is_acknowledged),
    readAt: item.read_at,
    acknowledgedAt: item.acknowledged_at,
    relatedAlertId: relatedAlert?.id ?? item.related_alert_id,
    relatedAlert: relatedAlert,
    performanceScore: weightBreakdown.final_score ?? weightScore ?? performanceScore,
    alertDetails: {
      performanceScore: weightBreakdown.final_score ?? weightScore ?? performanceScore,
      alertTimestamp: relatedAlert?.timestamp || item.timestamp || item.data_timestamp,
      metricsUsed: {
        processedCount24h: metrics.processed_count_24h ?? null,
        processedSizeGb24h: metrics.processed_size_gb_24h ?? null,
        skippedCount24h: metrics.skipped_count_24h ?? null,
        rescanPendingCurrent: metrics.rescan_pending_current ?? null,
        rescanPending24h: metrics.rescan_pending_24h ?? null,
        vcpuCount: metrics.vcpu_count ?? null
      },
      thresholdsUsed: {
        expectedProcessedCountPerVcpu: thresholds.expected_processed_count_per_vcpu ?? null,
        expectedProcessedSizeGbPerVcpu: thresholds.expected_processed_size_gb_per_vcpu ?? null,
        expectedRescanReductionPerVcpu: thresholds.expected_rescan_reduction_per_vcpu ?? null,
        expectedRescanReductionTotal: thresholds.expected_rescan_reduction_total ?? null,
        expectedProcessedCountTotal: thresholds.expected_processed_count_total ?? null,
        expectedProcessedSizeGbTotal: thresholds.expected_processed_size_gb_total ?? null
      },
      weightBreakdown: {
        countPenalty: weightBreakdown.count_penalty ?? null,
        sizePenalty: weightBreakdown.size_penalty ?? null,
        skipMultiplier: weightBreakdown.skip_multiplier ?? null,
        rescanLoadMultiplier: weightBreakdown.rescan_load_multiplier ?? null,
        combinedPenalty: weightBreakdown.combined_penalty ?? null,
        finalScore: weightBreakdown.final_score ?? null
      },
      penalties: {
        skipMultiplier: penalties.skip_multiplier ?? null,
        rescanLoadMultiplier: penalties.rescan_load_multiplier ?? null,
        scanningLoadMultiplier: penalties.scanning_load_multiplier ?? null,
        combinedMultiplier: penalties.combined_multiplier ?? null
      }
    }
  };
};

const sortByCreatedAtDesc = (items) => {
  return [...items].sort((a, b) => {
    const aTime = new Date(a.createdAt || a.time || 0).getTime();
    const bTime = new Date(b.createdAt || b.time || 0).getTime();
    return bTime - aTime;
  });
};

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const { showSnackbar } = useSnackbar();
  const [notifications, setNotifications] = useState([]);
  const [summaryByCluster, setSummaryByCluster] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const inFlightNotificationsRef = useRef(new Set());
  const inFlightSummariesRef = useRef(new Set());
  const inFlightAlertDetailsRef = useRef(new Set());
  const lastNotificationsFetchRef = useRef({});
  const lastSummaryFetchRef = useRef({});

  const axiosConfig = useMemo(() => ({
    headers: {
      Authorization: `Bearer ${user?.token || ''}`
    }
  }), [user?.token]);

  const fetchClusterNames = useCallback(async () => {
    const response = await API.get('/cluster/info', axiosConfig);
    const clusters = response?.data?.data || [];
    return clusters.map((cluster) => cluster.clusterName).filter(Boolean);
  }, [axiosConfig]);

  const fetchNotificationsForCluster = useCallback(async (clusterName) => {
    const allNotifications = [];
    const limit = 500;
    let offset = 0;
    while (true) {
      const response = await API.get(`/notifications/${encodeURIComponent(clusterName)}`, {
        ...axiosConfig,
        params: { limit, offset }
      });
      const fetched = response?.data?.data?.notifications || [];
      allNotifications.push(...fetched);
      if (fetched.length < limit) {
        break;
      }
      offset += limit;
    }
    return allNotifications;
  }, [axiosConfig]);

  const fetchSummaryForCluster = useCallback(async (clusterName) => {
    const response = await API.get(`/notifications/${encodeURIComponent(clusterName)}/summary`, axiosConfig);
    return response?.data?.data || null;
  }, [axiosConfig]);

  const fetchSingleNotification = useCallback(async (clusterName, notificationId) => {
    try {
      const response = await API.get(
        `/notifications/${encodeURIComponent(clusterName)}/${notificationId}`,
        axiosConfig
      );
      const item = response?.data?.data;
      if (item) {
        const normalized = normalizeNotification(item);
        setNotifications((prev) =>
          prev.map((n) => (String(n.id) === String(notificationId) ? normalized : n))
        );
        return normalized;
      }
    } catch (error) {
      // Silently fail - notification may have been deleted
    }
    return null;
  }, [axiosConfig]);

  const fetchAlertDetailsForNotification = useCallback(async (notificationId, relatedAlertId) => {
    // Prevent duplicate in-flight requests
    if (inFlightAlertDetailsRef.current.has(notificationId)) {
      return null;
    }

    if (!relatedAlertId) {
      return null;
    }

    inFlightAlertDetailsRef.current.add(notificationId);

    const updateNotificationWithAlert = (alert, dataTimestamp, performanceScore) => {
      // Try multiple possible metadata field names
      const metadata = parseMetadata(
        alert.alert_metadata || alert.alertMetadata || alert.metadata || alert
      );
      const metrics = metadata?.metrics_used || metadata?.metricsUsed || {};
      const thresholds = metadata?.thresholds_used || metadata?.thresholdsUsed || {};
      const weightBreakdown = metadata?.weight_breakdown || metadata?.weightBreakdown || {};
      const penalties = metadata?.penalties || {};

      const updatedAlertDetails = {
        performanceScore: weightBreakdown.final_score ?? weightBreakdown.finalScore ?? alert.weight_score ?? alert.weightScore ?? performanceScore,
        alertTimestamp: alert.timestamp || dataTimestamp,
        metricsUsed: {
          processedCount24h: metrics.processed_count_24h ?? metrics.processedCount24h ?? null,
          processedSizeGb24h: metrics.processed_size_gb_24h ?? metrics.processedSizeGb24h ?? null,
          skippedCount24h: metrics.skipped_count_24h ?? metrics.skippedCount24h ?? null,
          rescanPendingCurrent: metrics.rescan_pending_current ?? metrics.rescanPendingCurrent ?? null,
          rescanPending24h: metrics.rescan_pending_24h ?? metrics.rescanPending24h ?? null,
          vcpuCount: metrics.vcpu_count ?? metrics.vcpuCount ?? null
        },
        thresholdsUsed: {
          expectedProcessedCountPerVcpu: thresholds.expected_processed_count_per_vcpu ?? thresholds.expectedProcessedCountPerVcpu ?? null,
          expectedProcessedSizeGbPerVcpu: thresholds.expected_processed_size_gb_per_vcpu ?? thresholds.expectedProcessedSizeGbPerVcpu ?? null,
          expectedRescanReductionPerVcpu: thresholds.expected_rescan_reduction_per_vcpu ?? thresholds.expectedRescanReductionPerVcpu ?? null,
          expectedRescanReductionTotal: thresholds.expected_rescan_reduction_total ?? thresholds.expectedRescanReductionTotal ?? null,
          expectedProcessedCountTotal: thresholds.expected_processed_count_total ?? thresholds.expectedProcessedCountTotal ?? null,
          expectedProcessedSizeGbTotal: thresholds.expected_processed_size_gb_total ?? thresholds.expectedProcessedSizeGbTotal ?? null
        },
        weightBreakdown: {
          countPenalty: weightBreakdown.count_penalty ?? weightBreakdown.countPenalty ?? null,
          sizePenalty: weightBreakdown.size_penalty ?? weightBreakdown.sizePenalty ?? null,
          skipMultiplier: weightBreakdown.skip_multiplier ?? weightBreakdown.skipMultiplier ?? null,
          rescanLoadMultiplier: weightBreakdown.rescan_load_multiplier ?? weightBreakdown.rescanLoadMultiplier ?? null,
          combinedPenalty: weightBreakdown.combined_penalty ?? weightBreakdown.combinedPenalty ?? null,
          finalScore: weightBreakdown.final_score ?? weightBreakdown.finalScore ?? null
        },
        penalties: {
          skipMultiplier: penalties.skip_multiplier ?? penalties.skipMultiplier ?? null,
          rescanLoadMultiplier: penalties.rescan_load_multiplier ?? penalties.rescanLoadMultiplier ?? null,
          scanningLoadMultiplier: penalties.scanning_load_multiplier ?? penalties.scanningLoadMultiplier ?? null,
          combinedMultiplier: penalties.combined_multiplier ?? penalties.combinedMultiplier ?? null
        }
      };

      setNotifications((prev) =>
        prev.map((n) =>
          String(n.id) === String(notificationId)
            ? { ...n, alertDetails: updatedAlertDetails, performanceScore: updatedAlertDetails.performanceScore }
            : n
        )
      );
      inFlightAlertDetailsRef.current.delete(notificationId);
      return updatedAlertDetails;
    };

    // Try single alert endpoint first
    try {
      const response = await API.get(`/alerts/${relatedAlertId}`, axiosConfig);
      const alert = response?.data?.data || response?.data;
      if (alert && (alert.alert_metadata || alert.metadata || alert.id)) {
        // Get current notification data from state using functional update pattern
        let dataTimestamp = null;
        let performanceScore = null;
        setNotifications((prev) => {
          const n = prev.find((item) => String(item.id) === String(notificationId));
          dataTimestamp = n?.dataTimestamp;
          performanceScore = n?.performanceScore;
          return prev;
        });
        return updateNotificationWithAlert(alert, dataTimestamp, performanceScore);
      }
    } catch (error) {
      // Single alert endpoint may not exist, try fetching from list
    }

    // Fallback: fetch alerts list and find matching alert
    try {
      const response = await API.get('/alerts', {
        ...axiosConfig,
        params: { limit: 500, offset: 0 }
      });
      const alerts = response?.data?.data?.alerts || [];
      const matchingAlert = alerts.find((a) => a.id === relatedAlertId);
      if (matchingAlert) {
        let dataTimestamp = null;
        let performanceScore = null;
        setNotifications((prev) => {
          const n = prev.find((item) => String(item.id) === String(notificationId));
          dataTimestamp = n?.dataTimestamp;
          performanceScore = n?.performanceScore;
          return prev;
        });
        return updateNotificationWithAlert(matchingAlert, dataTimestamp, performanceScore);
      }
    } catch (error) {
      // Alerts endpoint may not be available
    }

    inFlightAlertDetailsRef.current.delete(notificationId);
    return null;
  }, [axiosConfig]);

  const refreshNotifications = useCallback(async (clusterName = null) => {
    if (!user?.token) {
      return;
    }
    const now = Date.now();
    if (clusterName) {
      const lastFetch = lastNotificationsFetchRef.current[clusterName] || 0;
      if (now - lastFetch < 30000) {
        return;
      }
      lastNotificationsFetchRef.current[clusterName] = now;
    } else {
      const lastFetch = lastNotificationsFetchRef.current.__all__ || 0;
      if (now - lastFetch < 30000) {
        return;
      }
      lastNotificationsFetchRef.current.__all__ = now;
    }
    if (clusterName) {
      if (inFlightNotificationsRef.current.has(clusterName)) {
        return;
      }
      inFlightNotificationsRef.current.add(clusterName);
    } else if (inFlightNotificationsRef.current.has('__all__')) {
      return;
    } else {
      inFlightNotificationsRef.current.add('__all__');
    }
    setIsLoading(true);
    try {
      const clusterNames = clusterName ? [clusterName] : await fetchClusterNames();
      const responses = await Promise.all(
        clusterNames.map((name) => fetchNotificationsForCluster(name))
      );
      const normalized = responses.flat().map(normalizeNotification);
      setNotifications((prev) => {
        if (!clusterName) {
          return sortByCreatedAtDesc(normalized);
        }
        const filtered = prev.filter((item) => item.cluster !== clusterName);
        return sortByCreatedAtDesc([...filtered, ...normalized]);
      });

      const summaries = await Promise.all(
        clusterNames.map((name) => fetchSummaryForCluster(name))
      );
      const summaryMap = {};
      summaries.forEach((summary, index) => {
        if (summary) {
          summaryMap[clusterNames[index]] = summary;
        }
      });
      setSummaryByCluster((prev) => ({ ...prev, ...summaryMap }));
    } catch (error) {
      showSnackbar?.('Failed to load notifications.');
    } finally {
      setIsLoading(false);
      if (clusterName) {
        inFlightNotificationsRef.current.delete(clusterName);
      } else {
        inFlightNotificationsRef.current.delete('__all__');
      }
    }
  }, [fetchClusterNames, fetchNotificationsForCluster, fetchSummaryForCluster, showSnackbar, user?.token]);

  const refreshSummaries = useCallback(async (clusterNames, forceRefresh = false) => {
    if (!user?.token || !clusterNames?.length) {
      return;
    }
    const now = Date.now();
    const pending = clusterNames.filter((name) => !inFlightSummariesRef.current.has(name));
    if (!pending.length) {
      return;
    }
    const throttled = pending.filter((name) => {
      // Skip clusters we already have summaries for (unless forcing refresh)
      if (!forceRefresh && summaryByCluster[name]) {
        return false;
      }
      const lastFetch = lastSummaryFetchRef.current[name] || 0;
      if (now - lastFetch < 30000) {
        return false;
      }
      lastSummaryFetchRef.current[name] = now;
      return true;
    });
    if (!throttled.length) {
      return;
    }
    throttled.forEach((name) => inFlightSummariesRef.current.add(name));
    try {
      const summaries = await Promise.all(
        throttled.map((name) => fetchSummaryForCluster(name))
      );
      const summaryMap = {};
      summaries.forEach((summary, index) => {
        if (summary) {
          summaryMap[throttled[index]] = summary;
        }
      });
      setSummaryByCluster((prev) => ({ ...prev, ...summaryMap }));
    } catch (error) {
      showSnackbar?.('Failed to load notification summaries.');
    } finally {
      throttled.forEach((name) => inFlightSummariesRef.current.delete(name));
    }
  }, [fetchSummaryForCluster, showSnackbar, summaryByCluster, user?.token]);

  useEffect(() => {
    if (!user?.token) {
      setNotifications([]);
      setSummaryByCluster({});
    }
  }, [user?.token]);

  const markAsRead = useCallback(async (id) => {
    const notification = notifications.find((item) => String(item.id) === String(id));
    if (!notification) {
      return;
    }
    const wasUnread = !notification.isRead;
    try {
      await API.post(
        `/notifications/${encodeURIComponent(notification.cluster)}/${notification.id}/read`,
        null,
        axiosConfig
      );
      await fetchSingleNotification(notification.cluster, notification.id);
      // Update summaryByCluster to keep unread count in sync
      if (wasUnread) {
        setSummaryByCluster((prev) => {
          const clusterSummary = prev[notification.cluster];
          if (clusterSummary && clusterSummary.unread_count > 0) {
            return {
              ...prev,
              [notification.cluster]: {
                ...clusterSummary,
                unread_count: clusterSummary.unread_count - 1
              }
            };
          }
          return prev;
        });
      }
    } catch (error) {
      showSnackbar?.('Failed to mark notification as read.');
    }
  }, [axiosConfig, fetchSingleNotification, notifications, showSnackbar]);

  const markAcknowledged = useCallback(async (id) => {
    const notification = notifications.find((item) => String(item.id) === String(id));
    if (!notification) {
      return;
    }
    const wasUnread = !notification.isRead;
    try {
      await API.post(
        `/notifications/${encodeURIComponent(notification.cluster)}/${notification.id}/acknowledge`,
        null,
        axiosConfig
      );
      await fetchSingleNotification(notification.cluster, notification.id);
      // Acknowledging also marks as read, so update summaryByCluster
      if (wasUnread) {
        setSummaryByCluster((prev) => {
          const clusterSummary = prev[notification.cluster];
          if (clusterSummary && clusterSummary.unread_count > 0) {
            return {
              ...prev,
              [notification.cluster]: {
                ...clusterSummary,
                unread_count: clusterSummary.unread_count - 1
              }
            };
          }
          return prev;
        });
      }
    } catch (error) {
      showSnackbar?.('Failed to acknowledge notification.');
    }
  }, [axiosConfig, fetchSingleNotification, notifications, showSnackbar]);

  const resolveNotification = useCallback(async (id) => {
    const notification = notifications.find((item) => String(item.id) === String(id));
    if (!notification) {
      return;
    }
    try {
      await API.post(
        `/notifications/${encodeURIComponent(notification.cluster)}/${notification.id}/resolve`,
        {},
        axiosConfig
      );
      setNotifications((prev) => prev.filter((item) => String(item.id) !== String(id)));
      // Update summaryByCluster to keep alert count in sync
      const severity = notification.severity;
      if (severity && severity !== 'healthy') {
        setSummaryByCluster((prev) => {
          const clusterSummary = prev[notification.cluster];
          if (clusterSummary?.by_severity && clusterSummary.by_severity[severity] > 0) {
            return {
              ...prev,
              [notification.cluster]: {
                ...clusterSummary,
                by_severity: {
                  ...clusterSummary.by_severity,
                  [severity]: clusterSummary.by_severity[severity] - 1
                }
              }
            };
          }
          return prev;
        });
      }
    } catch (error) {
      showSnackbar?.('Failed to resolve notification.');
    }
  }, [axiosConfig, notifications, showSnackbar]);

  const dismissNotification = useCallback(async (id) => {
    const notification = notifications.find((item) => String(item.id) === String(id));
    if (!notification) {
      return;
    }
    try {
      await API.post(
        `/notifications/${encodeURIComponent(notification.cluster)}/${notification.id}/dismiss`,
        {},
        axiosConfig
      );
      setNotifications((prev) => prev.filter((item) => String(item.id) !== String(id)));
      // Update summaryByCluster to keep alert count in sync
      const severity = notification.severity;
      if (severity && severity !== 'healthy') {
        setSummaryByCluster((prev) => {
          const clusterSummary = prev[notification.cluster];
          if (clusterSummary?.by_severity && clusterSummary.by_severity[severity] > 0) {
            return {
              ...prev,
              [notification.cluster]: {
                ...clusterSummary,
                by_severity: {
                  ...clusterSummary.by_severity,
                  [severity]: clusterSummary.by_severity[severity] - 1
                }
              }
            };
          }
          return prev;
        });
      }
    } catch (error) {
      showSnackbar?.('Failed to dismiss notification.');
    }
  }, [axiosConfig, notifications, showSnackbar]);

  const markAllRead = useCallback(async (clusterName = null) => {
    if (!user?.token) {
      return;
    }
    const targetClusters = clusterName
      ? [clusterName]
      : [...new Set(notifications.map((item) => item.cluster).filter(Boolean))];
    try {
      await Promise.all(
        targetClusters.map((name) =>
          API.post(`/notifications/${encodeURIComponent(name)}/mark_all_read`, null, axiosConfig)
        )
      );
      setNotifications((prev) =>
        prev.map((item) => {
          if (clusterName && item.cluster !== clusterName) {
            return item;
          }
          return { ...item, isRead: true };
        })
      );
      setSummaryByCluster((prev) => {
        const updated = { ...prev };
        targetClusters.forEach((name) => {
          if (updated[name]) {
            updated[name] = { ...updated[name], unread_count: 0 };
          }
        });
        return updated;
      });
    } catch (error) {
      showSnackbar?.('Failed to mark notifications as read.');
    }
  }, [axiosConfig, notifications, showSnackbar, user?.token]);

  const setReviewStatus = useCallback(async (id, status) => {
    if (status === 'read') {
      await markAsRead(id);
      return;
    }
    if (status === 'acknowledged') {
      await markAcknowledged(id);
      return;
    }
    if (status === 'resolved') {
      await resolveNotification(id);
      return;
    }
    if (status === 'dismissed') {
      await dismissNotification(id);
      return;
    }
    showSnackbar?.('Reverting review status is not supported yet.');
  }, [dismissNotification, markAcknowledged, markAsRead, resolveNotification, showSnackbar]);

  const unreadCount = useMemo(() => {
    const summaryValues = Object.values(summaryByCluster);
    if (summaryValues.length) {
      return summaryValues.reduce((total, summary) => total + (summary.unread_count || 0), 0);
    }
    return notifications.filter((item) => !item.isRead).length;
  }, [notifications, summaryByCluster]);

  const getClusterAlertCount = useCallback((clusterName) => {
    const summary = summaryByCluster[clusterName];
    if (summary?.by_severity) {
      const { warning = 0, degraded = 0, critical = 0, severe = 0 } = summary.by_severity;
      return warning + degraded + critical + severe;
    }
    return notifications.filter(
      (item) => item.cluster === clusterName && item.severity !== 'healthy'
    ).length;
  }, [notifications, summaryByCluster]);

  const contextValue = useMemo(() => ({
    notifications,
    unreadCount,
    isLoading,
    summaryByCluster,
    getClusterAlertCount,
    refreshNotifications,
    refreshSummaries,
    fetchSingleNotification,
    fetchAlertDetailsForNotification,
    markAsRead,
    markAcknowledged,
    resolveNotification,
    dismissNotification,
    setReviewStatus,
    markAllRead
  }), [
    notifications,
    unreadCount,
    isLoading,
    summaryByCluster,
    getClusterAlertCount,
    refreshNotifications,
    refreshSummaries,
    fetchSingleNotification,
    fetchAlertDetailsForNotification,
    markAsRead,
    markAcknowledged,
    resolveNotification,
    dismissNotification,
    setReviewStatus,
    markAllRead
  ]);

  return (
    <NotificationContext.Provider value={contextValue}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
};
